import { createParser, createSerializer } from 'nuqs';
import { z } from 'zod';

export const SUB_REPORT_QUERY_KEY = 'sr' as const;

const subReportFiltersSchema = z
  .object({
    from: z.string().min(1).optional(),
    to: z.string().min(1).optional(),
    consignment_id: z.string().min(1).optional()
  })
  .strict();

export const subReportPayloadSchemaV1 = z
  .object({
    v: z.literal(1),
    scope: z.literal('subr'),
    dashboard: z.string().min(1),
    widget: z.string().min(1),
    reportType: z.string().min(1),
    primary: z
      .object({
        key: z.string().min(1),
        value: z.string().min(1)
      })
      .strict(),
    filters: subReportFiltersSchema.optional()
  })
  .strict();

export type SubReportQueryPayloadV1 = z.infer<typeof subReportPayloadSchemaV1>;

interface BuildSubReportPayloadParams {
  dashboard: string;
  widget: string;
  reportType: string;
  primaryKey: string;
  primaryValue: string;
  from?: string;
  to?: string;
  consignmentId?: string;
}

interface ParseSubReportQueryParams {
  searchParams: URLSearchParams;
  dashboard?: string;
  widget?: string;
  reportType?: string;
}

export interface ParsedSubReportQuery {
  payload: SubReportQueryPayloadV1 | null;
  primaryKey: string;
  primaryValue: string;
  consignmentId?: string;
  from?: string;
  to?: string;
  isValid: boolean;
}

const encodeBase64Url = (value: string): string => {
  const utf8Bytes = new TextEncoder().encode(value);
  let binary = '';
  utf8Bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const decodeBase64Url = (value: string): string => {
  const padded = value.padEnd(Math.ceil(value.length / 4) * 4, '=');
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));

  return new TextDecoder().decode(bytes);
};

export const parseAsSubReportPayload = createParser<SubReportQueryPayloadV1>({
  parse: (queryValue) => {
    try {
      const decodedValue = decodeBase64Url(queryValue);
      const parsedJson = JSON.parse(decodedValue);
      const result = subReportPayloadSchemaV1.safeParse(parsedJson);

      return result.success ? result.data : null;
    } catch {
      return null;
    }
  },
  serialize: (value) => encodeBase64Url(JSON.stringify(subReportPayloadSchemaV1.parse(value))),
  eq: (a, b) => JSON.stringify(a) === JSON.stringify(b)
});

const serializeQuery = createSerializer({
  [SUB_REPORT_QUERY_KEY]: parseAsSubReportPayload
});

export const buildSubReportPayload = ({
  dashboard,
  widget,
  reportType,
  primaryKey,
  primaryValue,
  from,
  to,
  consignmentId
}: BuildSubReportPayloadParams): SubReportQueryPayloadV1 => {
  const filters =
    from || to || consignmentId
      ? {
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(consignmentId ? { consignment_id: consignmentId } : {})
        }
      : undefined;

  const nextPayload: SubReportQueryPayloadV1 = {
    v: 1,
    scope: 'subr',
    dashboard,
    widget,
    reportType,
    primary: {
      key: primaryKey,
      value: primaryValue
    },
    filters
  };

  return subReportPayloadSchemaV1.parse(nextPayload);
};

export const serializeSubReportQuery = (payload: SubReportQueryPayloadV1): string => {
  return serializeQuery({ [SUB_REPORT_QUERY_KEY]: payload });
};

export const parseSubReportQuery = ({
  searchParams,
  dashboard,
  widget,
  reportType
}: ParseSubReportQueryParams): ParsedSubReportQuery => {
  const queryValue = searchParams.get(SUB_REPORT_QUERY_KEY);
  if (!queryValue) {
    return {
      payload: null,
      primaryKey: '',
      primaryValue: '',
      isValid: false
    };
  }

  const payload = parseAsSubReportPayload.parse(queryValue);
  if (!payload) {
    return {
      payload: null,
      primaryKey: '',
      primaryValue: '',
      isValid: false
    };
  }

  if (
    (dashboard && payload.dashboard !== dashboard) ||
    (widget && payload.widget !== widget) ||
    (reportType && payload.reportType !== reportType)
  ) {
    return {
      payload: null,
      primaryKey: '',
      primaryValue: '',
      isValid: false
    };
  }

  return {
    payload,
    primaryKey: payload.primary.key,
    primaryValue: payload.primary.value,
    consignmentId: payload.filters?.consignment_id,
    from: payload.filters?.from,
    to: payload.filters?.to,
    isValid: true
  };
};
